/**
 * RC7 · M9 · Mobile Safety FAB + Safety Hub.
 *
 * ONE emergency-escape hatch anchored bottom-right on every mobile
 * route. Tap → opens a full-height sheet that provides:
 *
 *   1. Emergency Playbooks  (offline-safe · bundled)
 *   2. Emergency Contacts   (from Safety Memory)
 *   3. Safety Memory        (view — edit still via existing SafetyFab)
 *   4. Medical Information  (from Safety Memory · medical)
 *   5. Insurance Information (deep-link to Documents tab)
 *   6. Passport Information (deep-link to Documents tab · biometric-gated M6)
 *   7. Cruise Emergency Information (cruise-line emergency phone)
 *   8. Important Cruise Contacts (guest services)
 *   9. Consular / Embassy Information (advisory + link)
 *
 * The FAB itself must NEVER lie: if the network is down and no cached
 * hub payload exists, the FAB clearly indicates which sections are
 * not-yet-available, and the emergency playbooks (which are bundled)
 * remain readable.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldAlert, X, Phone, IdCard, ScrollText,
  Anchor, Landmark, MapPin, WifiOff, ChevronRight, PhoneCall,
  Info, PlaneTakeoff, LifeBuoy, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { isE164 } from "@/components/common/PhoneDisplay";
import { useCruise } from "@/context/CruiseContext";
import { EMERGENCY_PLAYBOOKS } from "@/mobile/safety/playbooks";

const RED = "#B91C1C";
const RED_DARK = "#7F1D1D";
const NAVY = "#0F2244";

// Local connectivity mirror — same reasoning as M6/M8 (avoid transitive
// pack.js dependency; keep the component self-contained).
function isOnline() {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

// One canonical hub-cache key so an offline load can still show the
// last-known cruise contacts + safety memory. Namespaced by user +
// voyage to prevent cross-tenant reads.
const HUB_CACHE_NS = "cc.rc7.m9.safety.hub.v1";
function _cacheKey(userId, voyageId) {
  return `${HUB_CACHE_NS}::${userId}::${voyageId}`;
}
function _readCache(userId, voyageId) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(_cacheKey(userId, voyageId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function _writeCache(userId, voyageId, payload) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(_cacheKey(userId, voyageId), JSON.stringify(payload));
  } catch { /* quota */ }
}


// ─── Playbook renderer ───────────────────────────────────────────────
function PlaybookCard({ playbook, onOpenDoc, contacts }) {
  const [expanded, setExpanded] = useState(false);
  const sevColor =
    playbook.severity === "critical" ? RED
      : playbook.severity === "urgent" ? "#B45309"
        : NAVY;
  return (
    <div
      data-testid={`safety-playbook-${playbook.id}`}
      className="rounded-2xl border overflow-hidden"
      style={{ borderColor: expanded ? sevColor : "#E2E8F0" }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-start justify-between gap-2 active:bg-slate-50"
        data-testid={`safety-playbook-toggle-${playbook.id}`}
      >
        <div className="min-w-0">
          <div
            className="text-[10px] tracking-[0.12em] uppercase font-semibold mb-1"
            style={{ color: sevColor }}
          >
            {playbook.severity === "critical" ? "Critical" : playbook.severity === "urgent" ? "Urgent" : "Advisory"}
          </div>
          <div className="text-sm font-semibold" style={{ color: NAVY }}>
            {playbook.title}
          </div>
        </div>
        <ChevronRight
          className={`w-4 h-4 mt-1 flex-shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
          style={{ color: sevColor }}
        />
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: "#F1F5F9" }}>
          <Section label="What to do now">
            <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
              {playbook.what_now.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </Section>
          {playbook.info_needed?.length > 0 && (
            <Section label="What you'll need">
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                {playbook.info_needed.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Section>
          )}
          {playbook.who_to_contact?.length > 0 && (
            <Section label="Who to contact">
              <div className="flex flex-wrap gap-1.5">
                {playbook.who_to_contact.map((who) => {
                  const c = resolveContact(who, contacts);
                  const key = `${playbook.id}-${who}`;
                  if (c?.phone) {
                    const dialable = isE164(c.phone);
                    const Tag = dialable ? "a" : "span";
                    return (
                      <Tag
                        key={key}
                        {...(dialable ? { href: `tel:${c.phone}` } : {})}
                        data-testid={`safety-playbook-call-${playbook.id}-${who}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full font-semibold"
                        style={{ background: RED, color: "white" }}
                      >
                        <PhoneCall className="w-3 h-3" /> {c.label}
                      </Tag>
                    );
                  }
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full"
                      style={{ background: "#F1F5F9", color: NAVY }}
                    >
                      {c?.label || who.replace(/_/g, " ")}
                    </span>
                  );
                })}
              </div>
            </Section>
          )}
          {playbook.open_documents?.length > 0 && (
            <Section label="Open document">
              <div className="flex flex-wrap gap-1.5">
                {playbook.open_documents.map((docType) => (
                  <button
                    key={docType}
                    type="button"
                    onClick={() => onOpenDoc(docType)}
                    data-testid={`safety-playbook-doc-${playbook.id}-${docType}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full"
                    style={{ background: "#EEF2FF", color: NAVY, border: "1px solid #C7D2FE" }}
                  >
                    {docType === "passport" ? <IdCard className="w-3 h-3" /> :
                     docType === "insurance" ? <ShieldCheck className="w-3 h-3" /> :
                     docType === "boarding_pass" ? <PlaneTakeoff className="w-3 h-3" /> :
                     <ScrollText className="w-3 h-3" />}
                    {docType.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </Section>
          )}
          {playbook.what_next?.length > 0 && (
            <Section label="What to do next">
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                {playbook.what_next.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}


// Given a token like "emergency_contact" or "ship_medical_centre",
// return `{label, phone|null}`. Never fabricates a phone — a null phone
// means the traveller hasn't provided the info yet.
function resolveContact(token, contacts) {
  const memory_first = contacts?.memory?.emergency_contacts?.[0];
  const memory_second = contacts?.memory?.emergency_contacts?.[1];
  const cruise_line = contacts?.cruise_line_contacts;
  const physician = contacts?.memory?.medical?.primary_physician;
  const LOOKUP = {
    emergency_contact:            { label: memory_first?.name || "Emergency contact", phone: memory_first?.phone },
    emergency_contact_secondary:  { label: memory_second?.name || "Secondary contact", phone: memory_second?.phone },
    primary_physician:            { label: physician?.name || "Primary physician", phone: physician?.phone },
    ship_medical_centre:          { label: "Ship medical centre", phone: null },
    ship_guest_services:          { label: "Guest services", phone: cruise_line?.guest_services_phone },
    ship_bridge:                  { label: "Ship bridge", phone: null },
    ship_security:                { label: "Ship security", phone: null },
    cruise_line_emergency:        { label: cruise_line?.cruise_line_name ? `${cruise_line.cruise_line_name} emergency` : "Cruise line emergency", phone: cruise_line?.emergency_phone },
    port_agent:                   { label: "Port agent", phone: null },
    consulate:                    { label: "Your consulate / embassy", phone: null },
    local_emergency:              { label: "Local emergency services", phone: null },
    insurance_emergency_assistance: { label: "Insurance emergency line", phone: null },
  };
  return LOOKUP[token] || { label: token.replace(/_/g, " "), phone: null };
}


// ─── Hub sheet ───────────────────────────────────────────────────────
function HubSheet({ open, onClose, userId, voyageId, setRoute }) {
  const [hub, setHub] = useState(() =>
    userId && voyageId ? _readCache(userId, voyageId) : null,
  );
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [error, setError] = useState(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!open || !userId || !voyageId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.get(
          `/api/mobile/safety/hub?voyage_id=${encodeURIComponent(voyageId)}`,
        );
        if (!cancelled) {
          setHub(r.data);
          _writeCache(userId, voyageId, r.data);
        }
      } catch (e) {
        if (!cancelled) setError(String(e?.response?.status || e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, voyageId]);

  const goDocuments = useCallback((docType) => {
    onClose();
    // Documents tab (M6). Anchor-only hop; the Documents surface picks
    // up the user's selected voyage from CruiseContext.
    setRoute && setRoute("documents");
    // Best-effort deep-hint (no drift risk — the Documents screen
    // sorts C3 first regardless).
    try {
      window.dispatchEvent(new CustomEvent("cc:doc_focus", { detail: { doc_type: docType } }));
    } catch { /* non-DOM env */ }
  }, [onClose, setRoute]);

  if (!open) return null;

  const memory = hub?.memory || { emergency_contacts: [], medical: null };
  const cruise = hub?.cruise_line_contacts || null;
  const missingHub = !hub && !loading;

  return (
    <div
      data-testid="safety-hub-sheet"
      className="fixed inset-0 z-50 flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Safety Hub"
    >
      {/* Header */}
      <header
        className="px-4 py-3 flex items-center justify-between"
        style={{ background: RED, color: "white" }}
      >
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-5 h-5" />
          <h2 className="text-base font-bold tracking-tight">Safety Hub</h2>
          {!online && (
            <span
              data-testid="safety-hub-offline-chip"
              className="ml-2 inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-white/15 border border-white/30"
            >
              <WifiOff className="w-3 h-3" /> Saved
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close Safety Hub"
          data-testid="safety-hub-close"
          className="w-9 h-9 -mr-1 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/20"
        >
          <X className="w-5 h-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-24">
        {/* 1. Emergency playbooks — ALWAYS available (bundled offline) */}
        <section data-testid="safety-hub-section-playbooks">
          <h3 className="text-xs tracking-[0.12em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
            Emergency Playbooks
          </h3>
          <p className="text-[11px] text-slate-500 mb-3 flex items-center gap-1">
            <Info className="w-3 h-3" /> Available offline — always readable.
          </p>
          <div className="space-y-2">
            {EMERGENCY_PLAYBOOKS.map((pb) => (
              <PlaybookCard
                key={pb.id}
                playbook={pb}
                onOpenDoc={goDocuments}
                contacts={hub}
              />
            ))}
          </div>
        </section>

        {/* 2. Emergency contacts */}
        <section data-testid="safety-hub-section-contacts">
          <h3 className="text-xs tracking-[0.12em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
            Your Emergency Contacts
          </h3>
          {missingHub && !online && (
            <NotAvailableOffline testId="safety-hub-contacts-offline" />
          )}
          {(memory.emergency_contacts || []).length === 0 && hub && (
            <p data-testid="safety-hub-no-contacts" className="text-xs text-slate-500">
              You haven't saved an emergency contact yet. Add one in Safety Memory.
            </p>
          )}
          <div className="space-y-2">
            {(memory.emergency_contacts || []).map((c, i) => (
              <ContactRow key={i} contact={c} testId={`safety-hub-contact-${i}`} />
            ))}
          </div>
        </section>

        {/* 3+4. Medical Information */}
        <section data-testid="safety-hub-section-medical">
          <h3 className="text-xs tracking-[0.12em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
            Medical Information
          </h3>
          {missingHub && !online && (
            <NotAvailableOffline testId="safety-hub-medical-offline" />
          )}
          {hub && <MedicalCard medical={memory.medical} />}
        </section>

        {/* 5. Insurance · deep-link to Documents */}
        <section data-testid="safety-hub-section-insurance">
          <QuickLinkRow
            icon={ShieldCheck}
            label="Insurance Information"
            hint="View your travel insurance descriptor"
            onClick={() => goDocuments("insurance")}
            testId="safety-hub-link-insurance"
          />
        </section>

        {/* 6. Passport · deep-link to Documents (biometric-gated on reveal) */}
        <section data-testid="safety-hub-section-passport">
          <QuickLinkRow
            icon={IdCard}
            label="Passport Information"
            hint="Unlocks with Face ID / fingerprint"
            onClick={() => goDocuments("passport")}
            testId="safety-hub-link-passport"
          />
        </section>

        {/* 7. Cruise Emergency Info */}
        <section data-testid="safety-hub-section-cruise-emergency">
          <h3 className="text-xs tracking-[0.12em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
            Cruise Emergency
          </h3>
          {missingHub && !online && (
            <NotAvailableOffline testId="safety-hub-cruise-offline" />
          )}
          {hub && (
            <CruiseContactCard
              label={cruise?.cruise_line_name
                ? `${cruise.cruise_line_name} 24-hour Emergency`
                : "Cruise line 24-hour Emergency"}
              phone={cruise?.emergency_phone}
              icon={Anchor}
              testId="safety-hub-cruise-emergency"
            />
          )}
        </section>

        {/* 8. Important Cruise Contacts */}
        <section data-testid="safety-hub-section-cruise-contacts">
          <h3 className="text-xs tracking-[0.12em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
            Important Cruise Contacts
          </h3>
          {hub && (
            <CruiseContactCard
              label="Guest services"
              phone={cruise?.guest_services_phone}
              icon={Anchor}
              testId="safety-hub-cruise-guest-services"
            />
          )}
        </section>

        {/* 9. Consulate / Embassy */}
        <section data-testid="safety-hub-section-consulate">
          <h3 className="text-xs tracking-[0.12em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
            Consulate / Embassy
          </h3>
          <div className="rounded-2xl border p-4 space-y-2" style={{ borderColor: "#E2E8F0" }}>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Landmark className="w-4 h-4 text-slate-400" />
              Locate the nearest embassy or consulate for your citizenship.
            </div>
            <a
              href="https://travel.state.gov/content/travel/en/international-travel/emergencies/emergencies-abroad.html"
              target="_blank" rel="noreferrer"
              data-testid="safety-hub-link-consulate"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full"
              style={{ background: "#EEF2FF", color: NAVY, border: "1px solid #C7D2FE" }}
            >
              <MapPin className="w-3 h-3" /> Find an embassy
            </a>
            <p className="text-[11px] text-slate-500">
              Requires an internet connection.
            </p>
          </div>
        </section>

        {error && (
          <div data-testid="safety-hub-error" className="text-xs px-3 py-2 rounded-md bg-red-50 text-red-700 border border-red-200">
            We couldn't refresh Safety Hub. Playbooks and any saved contacts are still available.
          </div>
        )}
      </div>
    </div>
  );
}


function NotAvailableOffline({ testId }) {
  return (
    <div
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-amber-800 bg-amber-50 border border-amber-200"
    >
      <WifiOff className="w-3.5 h-3.5" />
      Available when you're back online
    </div>
  );
}

function ContactRow({ contact, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border p-3 flex items-center gap-3"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#FEF2F2" }}>
        <PhoneCall className="w-4 h-4" style={{ color: RED }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: NAVY }}>
          {contact.name || "Emergency contact"}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {[contact.relationship, contact.phone].filter(Boolean).join(" · ")}
        </div>
      </div>
      {contact.phone && isE164(contact.phone) && (
        <a
          href={`tel:${contact.phone}`}
          aria-label={`Call ${contact.name || "emergency contact"}`}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: RED, color: "white" }}
        >
          <Phone className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function MedicalCard({ medical }) {
  if (!medical) return null;
  const rows = [];
  if (medical.blood_type) rows.push(["Blood type", medical.blood_type]);
  if (medical.allergies?.length) rows.push(["Allergies", medical.allergies.join(", ")]);
  if (medical.medications?.length) rows.push(["Medications", medical.medications.join(", ")]);
  if (medical.conditions?.length) rows.push(["Conditions", medical.conditions.join(", ")]);
  if (medical.primary_physician?.name) rows.push(["Primary physician", medical.primary_physician.name]);
  if (rows.length === 0) {
    return (
      <p data-testid="safety-hub-no-medical" className="text-xs text-slate-500">
        No medical information saved yet. Add it in Safety Memory.
      </p>
    );
  }
  return (
    <div className="rounded-2xl border p-3 space-y-1.5" style={{ borderColor: "#E2E8F0" }}>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 text-xs" data-testid={`safety-hub-medical-${k.toLowerCase().replace(/\s+/g,'-')}`}>
          <span className="text-slate-500 uppercase tracking-wide">{k}</span>
          <span className="text-slate-900 text-right break-words">{v}</span>
        </div>
      ))}
      {medical.primary_physician?.phone && isE164(medical.primary_physician.phone) && (
        <a
          href={`tel:${medical.primary_physician.phone}`}
          data-testid="safety-hub-medical-physician-call"
          className="mt-2 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full"
          style={{ background: RED, color: "white" }}
        >
          <PhoneCall className="w-3 h-3" /> Call {medical.primary_physician.name}
        </a>
      )}
    </div>
  );
}

function CruiseContactCard({ label, phone, icon: Icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border p-3 flex items-center gap-3"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#F1F5F9" }}>
        <Icon className="w-4 h-4" style={{ color: NAVY }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: NAVY }}>{label}</div>
        <div className="text-xs text-slate-500">{phone || "Number not on file"}</div>
      </div>
      {phone && isE164(phone) && (
        <a
          href={`tel:${phone}`}
          aria-label={`Call ${label}`}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: RED, color: "white" }}
        >
          <Phone className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function QuickLinkRow({ icon: Icon, label, hint, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full rounded-2xl border p-3 flex items-center gap-3 active:bg-slate-50"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#F1F5F9" }}>
        <Icon className="w-4 h-4" style={{ color: NAVY }} />
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-sm font-semibold" style={{ color: NAVY }}>{label}</div>
        <div className="text-xs text-slate-500 truncate">{hint}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </button>
  );
}


// ─── Exported FAB ────────────────────────────────────────────────────
export default function MobileSafetyFAB({ setRoute }) {
  const { voyage } = useCruise();
  const [open, setOpen] = useState(false);
  const userId = voyage?.owner_id || voyage?.owner_user_id || null;
  const voyageId = voyage?.id || null;

  // Hide when the sheet is open — the sheet has its own close button.
  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="safety-fab-mobile"
          aria-label="Open Safety Hub"
          className="md:hidden fixed bottom-24 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full shadow-lg ring-4 transition-transform active:scale-95"
          style={{ background: RED, color: "white", boxShadow: `0 10px 25px -5px ${RED_DARK}80` }}
        >
          <ShieldAlert className="h-6 w-6" strokeWidth={2.2} />
        </button>
      )}
      <HubSheet
        open={open}
        onClose={() => setOpen(false)}
        userId={userId}
        voyageId={voyageId}
        setRoute={setRoute}
      />
    </>
  );
}
